import type { AcceptableValue } from '@nuxt/ui'
import * as v from 'valibot'
import {
  computed,
  inject,
  provide,
  reactive,
  toValue,
  watch,
  type InjectionKey,
  type MaybeRefOrGetter,
} from 'vue'
import type { RowSchema } from '../utils/claim-editor'

/* null is excluded because Nuxt UI inputs don't accept it on v-model; empty is undefined */
export type FieldValue = Exclude<AcceptableValue, null> | undefined

const isBlank = (value: unknown) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '')

const fieldSchema = ({ label, isRequired, dataType }: RowSchema) =>
  v.pipe(
    v.unknown(),
    v.check((value) => !isRequired || !isBlank(value), `${label} is required`),
    v.check(
      (value) =>
        dataType !== 'number' ||
        isBlank(value) ||
        Number.isFinite(Number(value)),
      `${label} is not a valid number value`,
    ),
  )

/* Every key submitted is declared: rows, plus the per-line `is_other_claim` flag and the insights toggle that have no row */
export const buildClaimSchema = (rows: RowSchema[]) => {
  const sequences = new Set(
    rows.flatMap(({ name }) => name.match(/-(\d+)$/)?.[1] ?? []),
  )

  return v.object({
    include_nonactionable_insight_types: v.optional(v.boolean()),
    ...Object.fromEntries(
      [...sequences].map((n) => [
        `is_other_claim-${n}`,
        v.optional(v.boolean()),
      ]),
    ),
    ...Object.fromEntries(
      rows.map((row) => [
        row.name,
        row.isEditable ? fieldSchema(row) : v.optional(v.unknown()),
      ]),
    ),
  })
}

/*
 * Form state for `UForm`: seeds each editable row's `modelValue` once, when
 * the row first appears, and tracks edits against that value.
 */
export const useClaimForm = (rows: MaybeRefOrGetter<RowSchema[]>) => {
  const state = reactive<Record<string, FieldValue>>({})
  const initialValues = new Map<string, FieldValue>()

  watch(
    () => [...toValue(rows)],
    (current) =>
      current
        .filter(({ isEditable }) => isEditable)
        .forEach(({ name, modelValue }) => {
          initialValues.set(name, modelValue)
          if (!(name in state)) state[name] = modelValue
        }),
    { immediate: true },
  )

  const schema = computed(() => buildClaimSchema(toValue(rows)))

  const setFieldValue = (name: string, value: FieldValue) => {
    state[name] = value
  }

  /* Clearing an input yields '' while an untouched empty row holds undefined */
  const blankIfUnset = (value: FieldValue) => value ?? ''

  const isFieldChanged = (name: string) =>
    blankIfUnset(state[name]) !== blankIfUnset(initialValues.get(name))

  return { state, schema, setFieldValue, isFieldChanged }
}

type ClaimForm = ReturnType<typeof useClaimForm>

const claimFormKey: InjectionKey<ClaimForm> = Symbol('claim-form')

export const provideClaimForm = (form: ClaimForm) => provide(claimFormKey, form)

export const useClaimFormContext = (): ClaimForm => {
  const form = inject(claimFormKey)
  if (!form) throw new Error('useClaimFormContext requires provideClaimForm')
  return form
}
